import { useEffect, useMemo } from 'preact/hooks';
import { BottomSheet } from './BottomSheet';
import { t } from '@/i18n';
import type { LocalStateStore } from '@/lib/local-state';
import type { UiPrefs } from '@/lib/ui-prefs';
import { computePersonalStats } from '@/stats/personal-stats';
import { ENGAGEMENT_BADGES } from '@/lib/engagement-badges';
import { BADGES as PERFORMANCE_BADGES } from '@/lib/badges';

export interface PersonalStatsOverlayProps {
  open: boolean;
  onClose: () => void;
  store: LocalStateStore;
  prefs: UiPrefs;
}

export function PersonalStatsOverlay({ open, onClose, store, prefs }: PersonalStatsOverlayProps) {
  const seenSnapshot = useMemo(() => prefs.getPersonalStatsLastSeenLongest(), [open]);
  const stats = useMemo(() => computePersonalStats(store.getState()), [open, store]);

  useEffect(() => {
    if (!open) return;
    return () => { prefs.setPersonalStatsLastSeenLongest(stats.longestStreak); };
  }, [open, prefs, stats.longestStreak]);

  return (
    <BottomSheet open={open} onClose={onClose} heightVariant="tall" ariaLabel={t('personalStats.title')}>
      <div class="px-6 pb-6">
        <p class="font-mono text-[10px] uppercase tracking-[0.22em] text-foil mb-1">
          {t('personalStats.title')}
        </p>

        <div class="grid grid-cols-3 gap-2 mt-3 mb-5">
          <TrioCard label={t('personalStats.daysPlayed')} value={String(stats.daysPlayed)} />
          <TrioCard
            label={t('personalStats.currentStreak')}
            value={stats.currentStreak >= 1 ? `🔥 ${stats.currentStreak}` : String(stats.currentStreak)}
          />
          <TrioCard label={t('personalStats.longestStreak')} value={String(stats.longestStreak)} />
        </div>

        <p class="font-mono text-[10px] uppercase tracking-[0.18em] text-text-dim mb-2">
          {t('personalStats.distribution')}
        </p>
        {stats.daysPlayed === 0 ? (
          <p class="text-text-2 mb-5">{t('personalStats.empty')}</p>
        ) : (
          <Distribution dist={stats.scoreDistribution} />
        )}

        <p class="font-mono text-[10px] uppercase tracking-[0.18em] text-text-dim mb-2 mt-5">
          {t('personalStats.engagementBadges')}
        </p>
        <div class="flex gap-1.5 flex-wrap mb-4">
          {ENGAGEMENT_BADGES.map((b) => {
            const earned = stats.longestStreak >= b.threshold;
            const newlyEarned = earned && b.threshold > seenSnapshot;
            return (
              <BadgeChip
                emoji={b.emoji}
                name={t(b.i18nKey as never)}
                earned={earned}
                newlyEarned={newlyEarned}
              />
            );
          })}
        </div>

        <p class="font-mono text-[10px] uppercase tracking-[0.18em] text-text-dim mb-2">
          {t('personalStats.performanceBadges')}
        </p>
        <div class="flex gap-1.5 flex-wrap">
          {PERFORMANCE_BADGES.map((b) => {
            const earned = stats.earnedPerformanceBadges.includes(b.id);
            return (
              <BadgeChip
                emoji={b.emoji}
                name={t(b.i18nKey as never)}
                earned={earned}
                newlyEarned={false}
              />
            );
          })}
        </div>
      </div>
    </BottomSheet>
  );
}

function TrioCard({ label, value }: { label: string; value: string }) {
  return (
    <div
      class="flex flex-col items-center justify-center px-2 py-3 rounded-md bg-text/5"
      aria-label={`${label}: ${value}`}
    >
      <span class="font-display tabular-nums text-xl text-text">{value}</span>
      <span class="font-mono text-[9px] uppercase tracking-[0.12em] text-text-dim mt-1 text-center">{label}</span>
    </div>
  );
}

function Distribution({ dist }: { dist: number[] }) {
  const max = Math.max(1, ...dist);
  const rows = dist.map((count, score) => ({ score, count })).reverse();
  return (
    <ul class="space-y-1 font-mono text-xs">
      {rows.map(({ score, count }) => (
        <li class="flex items-center gap-2">
          <span class="w-3 text-text-dim text-right">{score}</span>
          <span
            class="bg-foil/70 h-2 rounded-sm"
            style={{ width: `${(count / max) * 100}%`, minWidth: count > 0 ? '4px' : '0' }}
          />
          <span class="tabular-nums text-text-2 ml-auto">{count}</span>
        </li>
      ))}
    </ul>
  );
}

function BadgeChip({
  emoji, name, earned, newlyEarned,
}: { emoji: string; name: string; earned: boolean; newlyEarned: boolean }) {
  const cls = earned
    ? `badge-chip foil-text font-mono text-[10px] px-2 py-1 rounded-full border border-foil/60 ${newlyEarned ? 'badge-glow' : ''}`
    : 'font-mono text-[10px] px-2 py-1 rounded-full border border-dashed border-text-dim/40 text-text-dim';
  return (
    <span
      class={cls}
      aria-label={earned ? `${name}` : `${name} – ${t('personalStats.locked')}`}
    >
      {emoji} {name}
    </span>
  );
}
